import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { navigate } from "gatsby";
import { auth as authApi } from "../lib/endpoints";
import { closeAllStreams } from "../lib/sseRegistry";
import { resetCounts } from "../hooks/useIncomingCount";

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: () => {},
  refresh: async () => {},
  updateUser: () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Bootstrap: ask the server who we are. The access cookie authenticates the call; if it's
  // expired, the axios interceptor transparently calls /refresh and retries. Only if that also
  // fails (no/expired refresh cookie) do we end up unauthenticated.
  const refresh = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(me);
      return me;
    } catch (e) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /*
   * Put a user the server has just returned in place of the one held here — the answer to a photo
   * upload, say — so every avatar in the shell, the dashboard and the leaderboard changes on the
   * same render without a second round trip to /me. Only an object is accepted: an empty body must
   * never sign anyone out by accident, which is what `setUser(undefined)` would amount to.
   */
  const updateUser = useCallback((next) => {
    if (next && typeof next === "object") setUser(next);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authApi.login(email, password); // server sets the HttpOnly cookies
    setUser(res.user);
    return res.user;
  }, []);

  const logout = useCallback(async () => {
    // Cut the live SSE connections up front — deterministically, before anything can await or the
    // page navigates. Unmount cleanup alone isn't enough: it races the navigation and isn't
    // guaranteed to run first, and an SSE stream is authenticated only at the handshake, so an open
    // one keeps delivering on a session we've already walked away from until it's closed.
    closeAllStreams();
    // Counts live at module scope and would otherwise outlive the session (see resetCounts).
    resetCounts();
    // Then drop the user so AppShell falls back to its loading branch and unmounts the app pages.
    setUser(null);
    try {
      await authApi.logout(); // revoke the refresh-token family + clear cookies server-side
    } catch (e) {
      // sign out locally regardless of the network outcome
    }
    // A client-side navigation, NOT window.location. Setting user to null already makes AppShell
    // redirect here, so a hard assignment made it happen twice: the login page appeared, then the
    // whole document reloaded and rendered it again. `replace` also keeps the signed-in page out of
    // history, so Back can't return to it. Nothing here needs a full reload any more — the two
    // things a reload used to clean up, live streams and the shared counts, are both torn down
    // explicitly above.
    navigate("/login", { replace: true });
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
